import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Button,
    Form,
    Input,
    Toast,
    Segmented,
    Tag,
    Loading
} from 'antd-mobile';
import axiosInstance from "../axiosInstance";
import dayjs from 'dayjs';

const LogViewer = () => {
    const [allLogs, setAllLogs] = useState([]); // 存储所有日志
    const [filteredLogs, setFilteredLogs] = useState([]); // 存储筛选后的日志
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        level: '',
        bco_number: '',
        message: ''
    });

    // 处理ANSI颜色代码，提取实际的日志级别
    const cleanLevel = (level) => {
        return level.replace(/\u001b\[\d+m/g, '');  // 移除ANSI颜色代码
    };

    // 获取所有唯一的BCO numbers用于筛选
    const uniqueBcoNumbers = useMemo(() => {
        const numbers = allLogs
            .map(log => log.bco_number)
            .filter(bco => bco !== null && bco !== undefined);
        return [...new Set(numbers)];
    }, [allLogs]);

    useEffect(() => {
        fetchLogs();
    }, []);

    // 当筛选条件改变时更新过滤后的日志
    useEffect(() => {
        filterLogs();
    }, [filters, allLogs]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/logs');
            // 预处理日志数据
            const processedLogs = response.data.map((log, index) => ({
                ...log,
                key: index,
                cleanLevel: cleanLevel(log.level)
            }));
            setAllLogs(processedLogs);
            Toast.show({
                icon: 'success',
                content: 'Logs loaded successfully'
            });
        } catch (error) {
            console.error('Error fetching logs:', error);
            Toast.show({
                icon: 'fail',
                content: 'Failed to load logs'
            });
        } finally {
            setLoading(false);
        }
    };

    const filterLogs = () => {
        let filtered = [...allLogs];

        if (filters.level) {
            filtered = filtered.filter(log =>
                cleanLevel(log.level).includes(filters.level)
            );
        }

        if (filters.bco_number) {
            filtered = filtered.filter(log =>
                log.bco_number === filters.bco_number
            );
        }

        if (filters.message) {
            const searchTerm = filters.message.toLowerCase();
            filtered = filtered.filter(log =>
                log.message.toLowerCase().includes(searchTerm)
            );
        }

        setFilteredLogs(filtered);
    };

    const handleFilterChange = (type, value) => {
        setFilters(prev => ({
            ...prev,
            [type]: value
        }));
    };

    const LevelTag = ({ level }) => {
        const cleanedLevel = cleanLevel(level);
        const levelConfig = {
            'INFO': { color: '#52c41a' },
            'WARNING': { color: '#faad14'},
            'ERROR': { color: '#f5222d' },
            'CRITICAL': { color: '#722ed1' },
            'DEBUG': { color: '#666' }
        };

        const config = levelConfig[cleanedLevel] || levelConfig.INFO;
        return (
            <Tag
                color={config.color}
                style={{
                    backgroundColor: config.fill,
                    border: `1px solid ${config.color}`,
                    padding: '2px 8px'
                }}
            >
                {cleanedLevel}
            </Tag>
        );
    };

    return (
        <div className="p-4">
            <Card className="mb-4">
                <div className="flex flex-col gap-4">
                    <div>
                        <div className="mb-2 font-medium">Log Level</div>
                        <Segmented
                            value={filters.level}
                            onChange={value => handleFilterChange('level', value)}
                            options={[
                                { label: 'All', value: '' },
                                { label: 'Info', value: 'INFO' },
                                { label: 'Warning', value: 'WARNING' },
                                { label: 'Error', value: 'ERROR' },
                                { label: 'Debug', value: 'DEBUG' }
                            ]}
                        />
                    </div>

                    {uniqueBcoNumbers.length > 0 && (
                        <div>
                            <div className="mb-2 font-medium">BCO Number</div>
                            <Segmented
                                value={filters.bco_number}
                                onChange={value => handleFilterChange('bco_number', value)}
                                options={[
                                    { label: 'All', value: '' },
                                    ...uniqueBcoNumbers.map(bco => ({
                                        label: `${bco}`,
                                        value: bco
                                    }))
                                ]}
                            />
                        </div>
                    )}

                    <div>
                        <div className="mb-2 font-medium">Search</div>
                        <Input
                            placeholder="Search in messages..."
                            value={filters.message}
                            onChange={value => handleFilterChange('message', value)}
                            clearable
                        />
                    </div>
                </div>
            </Card>

            <Card>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-lg font-bold">System Logs</div>
                    <div className="flex items-center gap-2">
                        {loading && <Loading color="primary" />}
                        <Button
                            onClick={fetchLogs}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    {filteredLogs.map((log, index) => (
                        <div
                            key={index}
                            className="border rounded-lg p-3 hover:bg-gray-50 gap-2"
                        >
                            <div className="flex flex-wrap gap-2 mb-2 items-center">
                                <LevelTag level={log.level} />
                                {log.bco_number && (
                                    <Tag color='#108ee9'>
                                        {log.bco_number}
                                    </Tag>
                                )}
                                <span className="text-gray-500 text-sm">
                                    {dayjs(log.time).format('YYYY-MM-DD HH:mm:ss')}
                                </span>
                            </div>
                            <div className="text-sm pl-2 break-words">
                                {log.message}
                            </div>
                        </div>
                    ))}

                    {filteredLogs.length === 0 && !loading && (
                        <div className="text-center py-8 text-gray-500">
                            No logs found
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default LogViewer;